/**
 * us_locations.js — US state → major cities catalog for the "Alvo de Negócio"
 * geo filter. Iter99: previously only Florida was supported; now any US state
 * is selectable and curated major cities are surfaced per state. States with
 * no curated list still allow free-text city entry from the same control.
 *
 * Source: top-population cities per state (2024 census-derived). Each entry
 * already includes the ", ST" suffix so it can be sent straight to the
 * geocoding/search backend without extra formatting on the client.
 */
export const US_STATES = [
  { code: "AL", name: "Alabama" },        { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },        { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },     { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },    { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },        { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },         { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },       { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },           { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },       { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },          { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },      { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },       { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },       { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },     { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" }, { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },           { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },         { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },   { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },   { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },          { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },        { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },     { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },      { code: "WY", name: "Wyoming" },
];

// Major cities per state. Free-text city entry is available for any state
// (or for cities not in the curated list).
export const US_CITIES_BY_STATE = {
  AL: ["Birmingham, AL", "Montgomery, AL", "Mobile, AL", "Huntsville, AL", "Tuscaloosa, AL"],
  AK: ["Anchorage, AK", "Fairbanks, AK", "Juneau, AK"],
  AZ: ["Phoenix, AZ", "Tucson, AZ", "Mesa, AZ", "Chandler, AZ", "Scottsdale, AZ", "Tempe, AZ", "Glendale, AZ", "Gilbert, AZ"],
  AR: ["Little Rock, AR", "Fayetteville, AR", "Fort Smith, AR", "Bentonville, AR"],
  CA: ["Los Angeles, CA", "San Diego, CA", "San Jose, CA", "San Francisco, CA", "Fresno, CA", "Sacramento, CA", "Long Beach, CA", "Oakland, CA", "Bakersfield, CA", "Anaheim, CA", "Santa Ana, CA", "Riverside, CA", "Irvine, CA"],
  CO: ["Denver, CO", "Colorado Springs, CO", "Aurora, CO", "Fort Collins, CO", "Lakewood, CO", "Boulder, CO"],
  CT: ["Bridgeport, CT", "New Haven, CT", "Stamford, CT", "Hartford, CT", "Waterbury, CT"],
  DE: ["Wilmington, DE", "Dover, DE", "Newark, DE"],
  DC: ["Washington, DC"],
  FL: [
    "Orlando, FL", "Winter Park, FL", "Winter Garden, FL", "Kissimmee, FL",
    "Lake Mary, FL", "Sanford, FL", "Altamonte Springs, FL", "Apopka, FL",
    "Ocoee, FL", "Clermont, FL", "Miami, FL", "Miami Beach, FL", "Coral Gables, FL",
    "Doral, FL", "Fort Lauderdale, FL", "Hollywood, FL", "Pembroke Pines, FL",
    "Tampa, FL", "St. Petersburg, FL", "Clearwater, FL", "Brandon, FL",
    "Jacksonville, FL", "Gainesville, FL", "Tallahassee, FL",
    "Naples, FL", "Fort Myers, FL", "Cape Coral, FL", "Sarasota, FL",
    "Daytona Beach, FL", "West Palm Beach, FL", "Boca Raton, FL", "Boynton Beach, FL",
  ],
  GA: ["Atlanta, GA", "Augusta, GA", "Savannah, GA", "Athens, GA", "Columbus, GA", "Macon, GA"],
  HI: ["Honolulu, HI", "Hilo, HI", "Kailua, HI"],
  ID: ["Boise, ID", "Meridian, ID", "Nampa, ID", "Idaho Falls, ID"],
  IL: ["Chicago, IL", "Aurora, IL", "Naperville, IL", "Joliet, IL", "Rockford, IL", "Springfield, IL", "Peoria, IL"],
  IN: ["Indianapolis, IN", "Fort Wayne, IN", "Evansville, IN", "South Bend, IN", "Carmel, IN"],
  IA: ["Des Moines, IA", "Cedar Rapids, IA", "Davenport, IA", "Iowa City, IA"],
  KS: ["Wichita, KS", "Overland Park, KS", "Kansas City, KS", "Topeka, KS", "Olathe, KS"],
  KY: ["Louisville, KY", "Lexington, KY", "Bowling Green, KY", "Owensboro, KY"],
  LA: ["New Orleans, LA", "Baton Rouge, LA", "Shreveport, LA", "Lafayette, LA"],
  ME: ["Portland, ME", "Bangor, ME", "Lewiston, ME"],
  MD: ["Baltimore, MD", "Columbia, MD", "Germantown, MD", "Silver Spring, MD", "Annapolis, MD"],
  MA: ["Boston, MA", "Worcester, MA", "Springfield, MA", "Cambridge, MA", "Lowell, MA"],
  MI: ["Detroit, MI", "Grand Rapids, MI", "Ann Arbor, MI", "Lansing, MI", "Warren, MI", "Sterling Heights, MI"],
  MN: ["Minneapolis, MN", "Saint Paul, MN", "Rochester, MN", "Duluth, MN", "Bloomington, MN"],
  MS: ["Jackson, MS", "Gulfport, MS", "Southaven, MS", "Hattiesburg, MS"],
  MO: ["Kansas City, MO", "Saint Louis, MO", "Springfield, MO", "Columbia, MO"],
  MT: ["Billings, MT", "Missoula, MT", "Bozeman, MT", "Helena, MT"],
  NE: ["Omaha, NE", "Lincoln, NE", "Bellevue, NE"],
  NV: ["Las Vegas, NV", "Henderson, NV", "Reno, NV", "North Las Vegas, NV", "Sparks, NV"],
  NH: ["Manchester, NH", "Nashua, NH", "Concord, NH"],
  NJ: ["Newark, NJ", "Jersey City, NJ", "Paterson, NJ", "Elizabeth, NJ", "Edison, NJ", "Trenton, NJ"],
  NM: ["Albuquerque, NM", "Las Cruces, NM", "Santa Fe, NM", "Rio Rancho, NM"],
  NY: ["New York, NY", "Buffalo, NY", "Rochester, NY", "Yonkers, NY", "Syracuse, NY", "Albany, NY", "White Plains, NY"],
  NC: ["Charlotte, NC", "Raleigh, NC", "Greensboro, NC", "Durham, NC", "Winston-Salem, NC", "Asheville, NC"],
  ND: ["Fargo, ND", "Bismarck, ND", "Grand Forks, ND"],
  OH: ["Columbus, OH", "Cleveland, OH", "Cincinnati, OH", "Toledo, OH", "Akron, OH", "Dayton, OH"],
  OK: ["Oklahoma City, OK", "Tulsa, OK", "Norman, OK", "Broken Arrow, OK"],
  OR: ["Portland, OR", "Eugene, OR", "Salem, OR", "Gresham, OR", "Hillsboro, OR", "Beaverton, OR", "Bend, OR"],
  PA: ["Philadelphia, PA", "Pittsburgh, PA", "Allentown, PA", "Erie, PA", "Reading, PA", "Scranton, PA"],
  RI: ["Providence, RI", "Cranston, RI", "Warwick, RI"],
  SC: ["Columbia, SC", "Charleston, SC", "North Charleston, SC", "Greenville, SC", "Myrtle Beach, SC"],
  SD: ["Sioux Falls, SD", "Rapid City, SD"],
  TN: ["Nashville, TN", "Memphis, TN", "Knoxville, TN", "Chattanooga, TN", "Murfreesboro, TN"],
  TX: ["Houston, TX", "San Antonio, TX", "Dallas, TX", "Austin, TX", "Fort Worth, TX", "El Paso, TX", "Arlington, TX", "Plano, TX", "Corpus Christi, TX", "Lubbock, TX", "Irving, TX", "Frisco, TX", "McKinney, TX"],
  UT: ["Salt Lake City, UT", "West Valley City, UT", "Provo, UT", "West Jordan, UT", "Orem, UT"],
  VT: ["Burlington, VT", "South Burlington, VT", "Montpelier, VT"],
  VA: ["Virginia Beach, VA", "Norfolk, VA", "Chesapeake, VA", "Richmond, VA", "Arlington, VA", "Alexandria, VA"],
  WA: ["Seattle, WA", "Spokane, WA", "Tacoma, WA", "Vancouver, WA", "Bellevue, WA", "Everett, WA", "Kent, WA"],
  WV: ["Charleston, WV", "Huntington, WV", "Morgantown, WV"],
  WI: ["Milwaukee, WI", "Madison, WI", "Green Bay, WI", "Kenosha, WI", "Racine, WI"],
  WY: ["Cheyenne, WY", "Casper, WY", "Laramie, WY"],
};

// Inverse lookup: given a location string ("Orlando, FL"), return the state code.
export function stateCodeFromCity(cityStr) {
  if (!cityStr) return "";
  const m = cityStr.match(/,\s*([A-Z]{2})\s*$/);
  return m ? m[1] : "";
}
